import { ALL_CENTRES } from '@appTypes/auth';
import { ScopeGate } from '@components/feedback/ScopeGate';
import { AddCentreDialog } from '@components/layout/topbar/AddCentreDialog';
import { AddOutlined, DomainAddOutlined } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useAppShellStore } from '@store/appShellStore';
import { useAuthStore } from '@store/authStore';
import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { CentrePickerGate } from './CentrePickerGate';

/**
 * Gate for EVERY page, Dashboard included. Admissions, installments, students
 * and receipts all carry a required `centreId`, so a scope with no centre behind
 * it makes the API answer NO_CENTRE and the page renders a bare "failed to load".
 * Two ways to get there:
 *
 *   1. The selected main centre has no active centres at all — e.g. one was just
 *      created and nothing added under it. Admins get an inline "Add Centre";
 *      staff can't create centres, so they're told who to ask.
 *   2. Centres exist but none is selected (a transient state right after
 *      switching main centre, or a bootstrap that failed) — offer the picker.
 *
 * The "All Centres" rollup is a legitimate scope here; whether a given page can
 * handle it is SingleCentreRoute's call, further down the tree.
 */
export const CentreRequiredRoute = () => {
  const isAdmin = useAuthStore((s) => s.user?.role === 'admin');
  const { centres, centreId, mainCentres, mainCentreId, shellReady } = useAppShellStore();
  const [addCentreOpen, setAddCentreOpen] = useState(false);

  const activeCentres = centres.filter((c) => c.active);
  const mainCentreName = mainCentres.find((m) => m._id === mainCentreId)?.name;

  // Wait for the bootstrap before judging — mid-load the lists are empty, and
  // this would otherwise flash the gate on every hard refresh.
  if (!shellReady) return <Outlet />;

  if (activeCentres.length === 0) {
    return (
      <>
        <ScopeGate
          Icon={DomainAddOutlined}
          title={mainCentreName ? `${mainCentreName} has no centres yet` : 'No centre available'}
          description={
            isAdmin
              ? 'Admissions, installments and receipts all belong to a specific centre, so this main centre needs at least one before anything can be recorded against it.'
              : 'Your main centre has no active centre yet. Please ask your administrator to add one before recording admissions or payments.'
          }
        >
          {isAdmin && (
            <Button
              onClick={() => setAddCentreOpen(true)}
              variant="contained"
              startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
              sx={(theme) => ({
                bgcolor: theme.palette.primary.main, borderRadius: '8px', fontSize: 13.5, fontWeight: 600,
                px: '20px', py: '9px', textTransform: 'none', boxShadow: 'none', mt: '4px',
                '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
              })}
            >
              Add Centre
            </Button>
          )}
        </ScopeGate>

        {/* Creating one fires `centres:changed`, whose handler in App.tsx reloads
            the shell onto the new centre. */}
        <AddCentreDialog open={addCentreOpen} onClose={() => setAddCentreOpen(false)} />
      </>
    );
  }

  if (centreId !== ALL_CENTRES && !centreId) {
    return (
      <CentrePickerGate
        title="Select a centre to continue"
        description="No centre is currently selected, so there's nothing to load this page against."
      />
    );
  }

  return <Outlet />;
};
